import { useEffect, useMemo, useState } from 'react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '~/components/ui/dialog';
import { useSaveAssignmentScores } from './useAssignmentMutations';
import type { ClassOfferingDetail } from './classDetailTypes';

type Assignment = NonNullable<ClassOfferingDetail['assignments']>[number];
type AssignmentScore = NonNullable<Assignment['scores']>[number];
type Person = NonNullable<ClassOfferingDetail['rosterEntries']>[number]['person'];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignment: Assignment;
  students: ReadonlyArray<Person>;
  onComplete: () => void;
}

interface AssignmentScoreEditorProps {
  assignment: Assignment;
  students: ReadonlyArray<Person>;
  onComplete: () => void | Promise<unknown>;
  onCancel?: () => void;
  resetKey?: unknown;
  showStudentName?: boolean;
}

interface Row {
  personId: string;
  firstName: string;
  lastName: string;
  score: string;
  comment: string;
  initialScore: string;
  initialComment: string;
  hasExistingScore: boolean;
  existingScoreId?: string;
  existingState?: AssignmentScore['state'];
  expectedLockVersion?: number;
}

type ScoreEntryState = 'UNGRADED' | 'GRADED' | 'MISSING' | 'EXCUSED';

type ParsedScore =
  | { kind: 'blank' }
  | { kind: 'excused' }
  | { kind: 'missing' }
  | { kind: 'graded'; points: number }
  | { kind: 'invalid' };

export function parseScoreEntry(value: string): ParsedScore {
  const normalized = value.trim();
  if (normalized === '') return { kind: 'blank' };
  if (normalized.toLowerCase() === 'e') return { kind: 'excused' };
  if (normalized.toLowerCase() === 'm') return { kind: 'missing' };

  // Number() is deliberately paired with a strict decimal shape so entries
  // such as "10 points" are rejected instead of silently becoming 10.
  if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalized)) return { kind: 'invalid' };

  const points = Number(normalized);
  if (!Number.isFinite(points) || points < 0) return { kind: 'invalid' };
  return { kind: 'graded', points };
}

function scoreEntryValue(existing: NonNullable<Assignment['scores']>[number] | undefined): string {
  if (!existing) return '';
  if (existing.state === 'GRADED') {
    return existing.pointsEarned == null ? '' : String(existing.pointsEarned);
  }
  if (existing.state === 'MISSING') return 'M';
  return 'E';
}

function scoreEntryState(row: Pick<Row, 'score'>): ScoreEntryState {
  const parsed = parseScoreEntry(row.score);
  if (parsed.kind === 'missing') return 'MISSING';
  if (parsed.kind === 'excused') return 'EXCUSED';
  if (parsed.kind === 'graded') return 'GRADED';
  return 'UNGRADED';
}

function scoreInputChanged(row: Row): boolean {
  return row.score.trim().toLowerCase() !== row.initialScore.trim().toLowerCase();
}

function rowIsDirty(row: Row): boolean {
  return scoreInputChanged(row) || row.comment.trim() !== row.initialComment.trim();
}

export function AssignmentScoreEditor({
  assignment,
  students,
  onComplete,
  onCancel,
  resetKey,
  showStudentName = true,
}: AssignmentScoreEditorProps) {
  const [rows, setRows] = useState<Row[]>([]);
  const [scoreSnapshot, setScoreSnapshot] = useState<ReadonlyArray<AssignmentScore>>(
    assignment.scores ?? []
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [saveScores] = useSaveAssignmentScores();

  const scoreByPerson = useMemo(() => {
    const m = new Map<string, NonNullable<Assignment['scores']>[number]>();
    scoreSnapshot.forEach((s) => {
      if (s.person?.id) m.set(s.person.id, s);
    });
    return m;
  }, [scoreSnapshot]);

  useEffect(() => {
    setScoreSnapshot(assignment.scores ?? []);
  }, [assignment.id, assignment.scores]);

  useEffect(() => {
    const sorted = [...students].sort((a, b) =>
      a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName)
    );
    setRows(
      sorted.map((s) => {
        const existing = scoreByPerson.get(s.id);
        const score = scoreEntryValue(existing);
        const comment = existing?.comment ?? '';
        return {
          personId: s.id,
          firstName: s.firstName,
          lastName: s.lastName,
          score,
          comment,
          initialScore: score,
          initialComment: comment,
          hasExistingScore: !!existing,
          existingScoreId: existing?.id,
          existingState: existing?.state,
          expectedLockVersion: existing?.lockVersion,
        };
      })
    );
  }, [students, scoreByPerson, resetKey]);

  useEffect(() => {
    setError(null);
  }, [assignment.id, resetKey]);

  const updateRow = (idx: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const updateScoreState = (idx: number, state: ScoreEntryState) => {
    if (state === 'MISSING') return updateRow(idx, { score: 'M' });
    if (state === 'EXCUSED') return updateRow(idx, { score: 'E' });
    if (state === 'UNGRADED') return updateRow(idx, { score: '', comment: '' });
    updateRow(idx, { score: '' });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = [];

      for (const row of rows) {
        if (!rowIsDirty(row)) continue;

        const parsed = parseScoreEntry(row.score);
        const comment = row.comment.trim();

        if (parsed.kind === 'invalid') {
          setError(`Enter a non-negative number, M, E, or blank for ${row.firstName} ${row.lastName}.`);
          return;
        }

        if (parsed.kind === 'blank') {
          if (comment) {
            setError(`Enter a score, M, or E before adding a comment for ${row.firstName} ${row.lastName}.`);
            return;
          }
          if (row.hasExistingScore) {
            payload.push({
              personId: row.personId,
              clear: true,
              expectedScoreId: row.existingScoreId!,
              expectedLockVersion: row.expectedLockVersion!,
            });
          }
          continue;
        }

        const expectation = row.hasExistingScore
          ? {
              expectedScoreId: row.existingScoreId!,
              expectedLockVersion: row.expectedLockVersion!,
            }
          : { expectAbsent: true as const };
        const preserveLegacyState = !scoreInputChanged(row) &&
          (row.existingState === 'MISSING' || row.existingState === 'EXEMPT');

        if (preserveLegacyState) {
          payload.push({
            personId: row.personId,
            state: row.existingState,
            comment: comment || null,
            ...expectation,
          });
        } else if (parsed.kind === 'excused') {
          payload.push({
            personId: row.personId,
            state: 'EXCUSED' as const,
            comment: comment || null,
            ...expectation,
          });
        } else if (parsed.kind === 'missing') {
          payload.push({
            personId: row.personId,
            state: 'MISSING' as const,
            comment: comment || null,
            ...expectation,
          });
        } else {
          payload.push({
            personId: row.personId,
            state: 'GRADED' as const,
            pointsEarned: parsed.points,
            comment: comment || null,
            ...expectation,
          });
        }
      }

      if (payload.length === 0) {
        setError('Nothing to save — enter at least one score.');
        setSaving(false);
        return;
      }

      const res = await saveScores({
        variables: { input: { assignmentId: assignment.id, scores: payload } },
      });
      const result = res.data?.saveAssignmentScores;
      const userErrors = result?.userErrors;
      if (userErrors?.length) {
        if (result?.assignment?.scores) {
          setScoreSnapshot(result.assignment.scores);
        }
        setError(userErrors.map((e: any) => e.message).join(', '));
        return;
      }
      await onComplete();
    } catch (e: any) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const hasDirtyRows = rows.some(rowIsDirty);

  return (
    <div className="space-y-3">
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          No current students belong to this assignment&apos;s gradebooks.
        </p>
      ) : (
        <div className="space-y-2">
          <div
            className={`grid gap-2 px-1 text-xs text-muted-foreground ${
              showStudentName ? 'grid-cols-[1.35fr_120px_110px_1.25fr]' : 'grid-cols-[120px_110px_1fr]'
            }`}
          >
            {showStudentName && <span>Student</span>}
            <span>Status</span>
            <span>Score</span>
            <span>Comment</span>
          </div>
          {rows.map((r, idx) => (
            <div
              key={r.personId}
              className={`grid items-center gap-2 ${
                showStudentName ? 'grid-cols-[1.35fr_120px_110px_1.25fr]' : 'grid-cols-[120px_110px_1fr]'
              }`}
            >
              {showStudentName && (
                <div className="text-sm font-medium">{r.lastName}, {r.firstName}</div>
              )}
              <Select
                value={scoreEntryState(r)}
                onValueChange={(state) => updateScoreState(idx, state as ScoreEntryState)}
              >
                <SelectTrigger aria-label={`Status for ${r.firstName} ${r.lastName}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UNGRADED">Ungraded</SelectItem>
                  <SelectItem value="GRADED">Points</SelectItem>
                  <SelectItem value="MISSING">Missing</SelectItem>
                  <SelectItem value="EXCUSED">Excused</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={r.score}
                onChange={(e) => updateRow(idx, { score: e.target.value })}
                placeholder="Points"
                aria-label={`Score for ${r.firstName} ${r.lastName}`}
                disabled={scoreEntryState(r) === 'MISSING' || scoreEntryState(r) === 'EXCUSED'}
              />
              <Input
                value={r.comment}
                onChange={(e) => updateRow(idx, { comment: e.target.value })}
                placeholder="Optional"
                aria-label={`Comment for ${r.firstName} ${r.lastName}`}
              />
            </div>
          ))}
        </div>
      )}

      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-end gap-2 border-t pt-3">
        {onCancel && (
          <Button variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        )}
        <Button onClick={handleSave} disabled={saving || rows.length === 0 || !hasDirtyRows}>
          {saving ? 'Saving…' : 'Save grades'}
        </Button>
      </div>
    </div>
  );
}

export default function GradeEntryDialog({
  open, onOpenChange, assignment, students, onComplete,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enter grades — {assignment.name}</DialogTitle>
          <DialogDescription>
            {assignment.pointsPossible} points possible
            {assignment.dueOn ? ` · due ${String(assignment.dueOn)}` : ''}
            <span className="mt-1 block">
              Enter points (including 0), choose Missing or Excused, or leave blank for ungraded.
              Missing counts as zero; blank and excused scores do not affect the grade.
            </span>
          </DialogDescription>
        </DialogHeader>

        <AssignmentScoreEditor
          assignment={assignment}
          students={students}
          onComplete={onComplete}
          onCancel={() => onOpenChange(false)}
          resetKey={open}
        />
      </DialogContent>
    </Dialog>
  );
}
